import { transform } from 'ol/proj.js';
import { getArea, getDistance } from 'ol/sphere.js';

const MAP_PROJECTION = 'EPSG:3857';
const DATA_PROJECTION = 'EPSG:4326';

export const groundDistance = (start, end) => getDistance(
  transform(start.slice(), MAP_PROJECTION, DATA_PROJECTION),
  transform(end.slice(), MAP_PROJECTION, DATA_PROJECTION)
);

const ellipseAxes = geometry => {
  const storedCenter = geometry.get('ellipseCenter');
  const storedMajorEnd = geometry.get('ellipseMajorEnd');
  const storedMinorEnd = geometry.get('ellipseMinorEnd');
  if (storedCenter && storedMajorEnd && storedMinorEnd) {
    return {
      center: storedCenter,
      majorEnd: storedMajorEnd,
      minorEnd: storedMinorEnd,
    };
  }

  const coordinates = geometry.getCoordinates()[0] || [];
  const vertices = coordinates.length > 1
    ? coordinates.slice(0, -1)
    : coordinates;
  if (!vertices.length) return null;

  const center = vertices.reduce(
    (sum, coordinate) => [
      sum[0] + coordinate[0],
      sum[1] + coordinate[1],
    ],
    [0, 0]
  ).map(value => value / vertices.length);

  const sorted = vertices
    .map(coordinate => ({
      coordinate,
      distance: Math.hypot(
        coordinate[0] - center[0],
        coordinate[1] - center[1]
      ),
    }))
    .sort((first, second) => first.distance - second.distance);

  return {
    center,
    minorEnd: sorted[0].coordinate,
    majorEnd: sorted[sorted.length - 1].coordinate,
  };
};

export const calculateShapeMeasurement = (feature, drawingType) => {
  if (!feature || !feature.getGeometry()) return null;
  const geometry = feature.getGeometry();

  if (drawingType === 'Circle' && geometry.getType() === 'Circle') {
    const center = geometry.getCenter();
    const radius = geometry.getRadius();
    if (!(radius > 0)) return null;

    const radiusMeters = groundDistance(
      center,
      [center[0] + radius, center[1]]
    );
    return {
      drawingType,
      radiusMeters,
      diameterMeters: radiusMeters * 2,
      areaSquareMeters: Math.PI * radiusMeters * radiusMeters,
    };
  }

  if (drawingType === 'Ellipse' && geometry.getType() === 'Polygon') {
    const axes = ellipseAxes(geometry);
    if (!axes) return null;

    const majorRadiusMeters = groundDistance(axes.center, axes.majorEnd);
    const minorRadiusMeters = groundDistance(axes.center, axes.minorEnd);
    if (!(majorRadiusMeters > 0) || !(minorRadiusMeters > 0)) return null;

    return {
      drawingType,
      majorAxisMeters: majorRadiusMeters * 2,
      minorAxisMeters: minorRadiusMeters * 2,
      areaSquareMeters: Math.abs(getArea(geometry, {
        projection: MAP_PROJECTION,
      })),
    };
  }

  return null;
};

export const formatDistance = meters => {
  if (!(meters >= 0)) return '—';
  if (meters >= 1000) return `${(meters / 1000).toFixed(2)} km`;
  if (meters < 100) return `${meters.toFixed(1)} m`;
  return `${Math.round(meters)} m`;
};

export const formatArea = squareMeters => {
  if (!(squareMeters >= 0)) return '—';
  if (squareMeters >= 1000000) {
    return `${(squareMeters / 1000000).toFixed(2)} km²`;
  }
  if (squareMeters < 10000) {
    return `${squareMeters.toFixed(1)} m²`;
  }
  return `${Math.round(squareMeters).toLocaleString('es-CL')} m²`;
};

export const measurementItems = measurement => {
  if (!measurement) return [];
  if (measurement.drawingType === 'Circle') {
    return [
      { label: 'Radio', value: formatDistance(measurement.radiusMeters) },
      { label: 'Diámetro', value: formatDistance(measurement.diameterMeters) },
      { label: 'Área aprox.', value: formatArea(measurement.areaSquareMeters) },
    ];
  }
  if (measurement.drawingType === 'Ellipse') {
    return [
      { label: 'Eje mayor', value: formatDistance(measurement.majorAxisMeters) },
      { label: 'Eje menor', value: formatDistance(measurement.minorAxisMeters) },
      { label: 'Área aprox.', value: formatArea(measurement.areaSquareMeters) },
    ];
  }
  return [];
};
